import { Component, OnDestroy, OnInit } from '@angular/core';
import { CityModel, CountryModel, getCurrencySymbol, HxCityInfoService, HxCityService, UiLabel } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { getFormErrors } from '@adm-web-app/utils';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxCountrySelectComponent, HxLabelEditComponent } from 'hx-components';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';

interface FormModel {
  enabled: boolean;
  title: UiLabel;
  countryId: number;
  order: number;
  slug: string;
  deliveryMinOrderPrice: number | null;
}

@Component({
    selector: 'hc-city-edit',
    templateUrl: './city-edit.component.html',
    styleUrls: ['./city-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxLabelEditComponent, NzRadioGroupComponent, NzRadioComponent, HxCountrySelectComponent, NzInputDirective, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, NzInputNumberComponent, TranslocoPipe]
})
export class CityEditComponent implements OnInit, OnDestroy {
  model!: CityModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;
  isLoading = {
    model: false,
  };
  isSaving = false;
  /** the money sign of the country of the city, for the label of the minimum */
  currencySymbol = '₸';
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modal: NzModalService,
    private cityService: HxCityService,
    private cityInfoService: HxCityInfoService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
  ) {
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as CityModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {enabled: true} as CityModel;
      this.isCreate = true;
    }
    this.form = this.fb.group({
      enabled: [null, [Validators.required]],
      title: [null, [Validators.required]],
      countryId: [null, [Validators.required]],
      order: [11, [Validators.required]],
      slug: [null, [Validators.required]],
      // not a field of the city: it lives in the property `city-info` and is saved by its own request
      deliveryMinOrderPrice: [null, [Validators.min(0)]],
    });
    this.updateForm();
    if (this.isEdit) {
      void this.loadDeliveryMinOrderPrice();
    }
  }

  submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      this.form.disable();
      const formModel = this.form.getRawValue() as FormModel;
      const model: Partial<CityModel> = {
        enabled: formModel.enabled,
        title: formModel.title,
        country: {id: formModel.countryId} as CountryModel,
        order: formModel.order,
        slug: formModel.slug,
      };
      model.id = this.model.id;
      this.isSaving = true;
      this.cityService.saveCity(model).subscribe(async result => {
        if (this.isEdit && this.form.controls['deliveryMinOrderPrice'].dirty) {
          await this.saveDeliveryMinOrderPrice(result.id, formModel.deliveryMinOrderPrice);
        }
        this.form.markAsPristine();
        this.notification.create(
          'success',
          'Город',
          this.tr.translate('nt.dataSaved')
        );
        if (this.isCreate) {
          this.router.navigate([`/cities/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.cityService.getCityById(result.id).subscribe(savedModel => {
            this.model = savedModel;
            this.isLoading.model = false;
            this.updateForm();
            void this.loadDeliveryMinOrderPrice();
          });
        }
        this.form.enable();
        this.isSaving = false;
      }, err => {
        this.form.enable();
        this.isSaving = false;
        this.notification.error(this.tr.translate('nav.Cities'), this.tr.translate(err.error?.message));
      });
    } else {
      console.log('formErrors', getFormErrors(this.form));
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  /** The minimum is the key `deliveryMinOrderPrice` of the property `city-info`; the other keys of the property are kept by the service. */
  private async loadDeliveryMinOrderPrice() {
    this.currencySymbol = getCurrencySymbol(this.model.country?.currency) || '₸';
    try {
      const price = await this.cityInfoService.getDeliveryMinOrderPrice(this.model.id);
      const control = this.form.controls['deliveryMinOrderPrice'];
      control.setValue(price, {emitEvent: false});
      control.markAsPristine();
    } catch {
      // the field stays empty; saving it again would overwrite the minimum, so a failed read is shown
      this.notification.error(this.tr.translate('nav.Cities'), this.tr.translate('cities.minOrder.loadFailed'));
      this.form.controls['deliveryMinOrderPrice'].disable();
    }
  }

  private async saveDeliveryMinOrderPrice(cityId: number, price: number | null) {
    try {
      await this.cityInfoService.saveDeliveryMinOrderPrice(cityId, price);
      this.form.controls['deliveryMinOrderPrice'].markAsPristine();
    } catch {
      this.notification.error(this.tr.translate('nav.Cities'), this.tr.translate('cities.minOrder.saveFailed'));
    }
  }

  private updateForm() {
    const formModel: Partial<FormModel> = {
      enabled: this.model.enabled,
      title: this.model.title,
      countryId: this.model.country?.id,
      order: this.model.order,
      slug: this.model.slug,
    };

    this.form.patchValue(formModel);
  }
}
